import { useToast } from '../components/Toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity as ActivityIcon, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { Filters, useFilters, type FilterField } from '../components/Filters';
import { SelectionBar, type BulkAction } from '../components/SelectionBar';
import { Modal } from '../components/Modal';
import { RowActions } from '../components/RowActions';
import { StatusPill, toneFor } from '../components/StatusPill';
import { TokenProgress } from '../components/TokenProgress';
import { useSession } from '../lib/session';
import type { PowerActivity as Row } from '../lib/types';

/**
 * Everything that has happened, in one table.
 *
 * ## Why one and not two
 *
 * Payments and forwarded messages were two panels because they are two
 * tables, which is a fact about the database and of no use to anybody working
 * the screen. Somebody asking "what happened with Keumbu this afternoon" read
 * a payment in one place, hunted its confirmation in another, and held the
 * order in their head — and the two panels sorted differently, filtered
 * differently and offered different verbs.
 *
 * One list now, newest first, with the filtering, sorting, selection, export
 * and printing the meter list has. What a row *is* became a column.
 *
 * ## Why the verbs differ by row
 *
 * Because the two are genuinely different things. A payment can be corrected,
 * retried or written off; a message can be filed under a landlord, corrected
 * or sent on. The API says what each row allows, so a button that could only
 * ever produce an error is never drawn.
 */
export function PowerActivity({
  onLandlord,
  onEdit,
  onFail,
  onAssign,
  onCorrect,
  onSend,
}: {
  onLandlord: (row: Row) => void;
  onEdit: (row: Row) => void;
  onFail: (row: Row) => void;
  onAssign: (row: Row) => void;
  onCorrect: (row: Row) => void;
  onSend: (row: Row) => void;
}) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [showTrash, setShowTrash] = useState(false);
  // Folded by default: the page is for the meters, this is opened to chase one.
  const [open, setOpen] = useState(false);
  /*
   * Failures are shown, not printed into the page.
   *
   * A red paragraph below the table is missed on a phone, appears under
   * whatever somebody was looking at, and can still be sitting there ten
   * minutes after it stopped being true. A dialog says its piece and is
   * dismissed on purpose.
   */
  const toast = useToast();
  const [stagesFor, setStagesFor] = useState<string | null>(null);

  const mayPay = can('payment:record');
  const mayEdit = can('subscription:write');

  const activity = useQuery({
    queryKey: ['power-activity', showTrash],
    queryFn: () => api.powerActivity({ trash: showTrash }),
    /*
     * Polled, because both halves arrive from outside this browser — a
     * confirmation from Safaricom, a message off the handset. Three seconds
     * is what the progress panel used, and somebody watching a payment land
     * is the reason this refreshes at all.
     */
    refetchInterval: 3_000,
    refetchIntervalInBackground: false,
  });

  /*
   * The four stages, for the one payment somebody is chasing.
   *
   * This used to be a panel under every in-flight row, and when the two lists
   * became one table it was left behind — the chip said "Waiting" and nothing
   * on the screen would say what it was waiting for. A list wants a word; a
   * person chasing one payment wants the account. So it is a dialog now, on
   * the rows that have stages to show.
   */
  const outstanding = useQuery({
    queryKey: ['power-outstanding'],
    queryFn: api.powerOutstanding,
    refetchInterval: 3_000,
    refetchIntervalInBackground: false,
  });

  /*
   * Whether an SMS can actually leave the building. The mock provider
   * succeeds, so the last stage would go green for a message nobody received.
   */
  const readiness = useQuery({ queryKey: ['power-readiness'], queryFn: api.powerReadiness });

  const stageRows = outstanding.data ?? [];
  const stagesRow = stageRows.find((one) => one.id === stagesFor) ?? null;

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['power-activity'] }),
      queryClient.invalidateQueries({ queryKey: ['power-meters'] }),
      queryClient.invalidateQueries({ queryKey: ['power-plan'] }),
    ]);
  };

  const retry = useMutation({
    mutationFn: (id: string) => api.retryPowerPurchase(id),
    onSuccess: refresh,
    onError: (e: Error) => toast.problem(e),
  });

  const discard = useMutation({
    mutationFn: (ids?: string[]) => api.discardAbandonedPayments(ids),
    onSuccess: async () => {
      setChosen(new Set());
      await refresh();
    },
    onError: (e: Error) => toast.problem(e),
  });

  const all = activity.data ?? [];

  const FIELDS: FilterField<Row>[] = [
    {
      key: 'what',
      label: 'Kind',
      type: 'choice',
      options: [
        { value: 'Payment', label: 'Payment' },
        { value: 'KPLC token', label: 'KPLC token' },
        { value: 'M-Pesa', label: 'M-Pesa' },
        { value: 'Unrecognised', label: 'Unrecognised' },
      ],
      value: (r) => r.what,
    },
    {
      key: 'state',
      label: 'State',
      type: 'choice',
      options: [
        { value: 'Waiting', label: 'Waiting' },
        { value: 'Paid', label: 'Paid' },
        { value: 'Token in', label: 'Token in' },
        { value: 'Sent', label: 'Sent' },
        { value: 'Failed', label: 'Failed' },
        { value: 'Needs a landlord', label: 'Needs a landlord' },
        { value: 'Ready to send', label: 'Ready to send' },
      ],
      value: (r) => r.state,
    },
    { key: 'meter', label: 'Meter', type: 'text', value: (r) => r.meterLabel },
    { key: 'number', label: 'Meter number', type: 'text', value: (r) => r.meterNumber },
    { key: 'landlord', label: 'Landlord', type: 'text', value: (r) => r.landlordName },
    { key: 'receipt', label: 'M-Pesa receipt', type: 'text', value: (r) => r.receipt },
    { key: 'sender', label: 'Sender', type: 'text', value: (r) => r.sender },
    { key: 'amount', label: 'Amount', type: 'number', value: (r) => (r.amountCents ?? 0) / 100 },
    { key: 'at', label: 'When', type: 'date', value: (r) => r.at },
  ];

  const { query, setQuery, clauses, setClauses, filtered } = useFilters(all, FIELDS);

  const actions: BulkAction<Row>[] = [
    { key: 'print', label: 'Print', prominent: true, run: () => window.print() },
    ...(mayPay
      ? [
          {
            key: 'discard',
            label: 'Remove abandoned payments',
            confirm: (picked: Row[]) =>
              `Remove ${picked.length} row${picked.length === 1 ? '' : 's'}? ` +
              'Only payments that never took money are removed — anything with a receipt, a ' +
              'confirmation or a token is left exactly where it is.',
            run: async (picked: Row[]) => {
              await discard.mutateAsync(
                picked.filter((one) => one.source === 'purchase').map((one) => one.id),
              );
            },
          },
        ]
      : []),
  ];

  const columns: Column<Row>[] = [
    {
      key: 'at',
      header: 'When',
      sortBy: (r) => r.at,
      cell: (r) => (
        <span>
          <span className="block">{new Date(r.at).toLocaleString('en-KE')}</span>
          <span className="block text-xs text-muted-foreground">
            {r.sender ? `from ${r.sender}` : r.payerPhone ? `paying from ${r.payerPhone}` : ''}
          </span>
        </span>
      ),
    },
    {
      key: 'what',
      header: 'What',
      sortBy: (r) => r.what,
      cell: (r) => (
        <span>
          <span className="font-medium">{r.what}</span>
          <span className="block text-xs text-muted-foreground">
            {[r.amount, r.token, r.receipt].filter(Boolean).join(' · ') || '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'meter',
      header: 'Meter',
      sortBy: (r) => r.meterLabel ?? null,
      cell: (r) =>
        r.meterLabel ? (
          <span>
            {r.meterLabel}
            <span className="block text-xs text-muted-foreground">{r.meterNumber}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">
            {r.meterNumber ?? '—'}
            <span className="block text-xs">not on file</span>
          </span>
        ),
    },
    {
      key: 'landlord',
      header: 'Landlord',
      secondary: true,
      sortBy: (r) => r.landlordName ?? null,
      cell: (r) => <span className="text-muted-foreground">{r.landlordName ?? '—'}</span>,
    },
    {
      key: 'state',
      header: 'State',
      sortBy: (r) => r.state,
      cell: (r) => (
        <span>
          <StateChip state={r.state} />
          {r.detail && (
            <span className="mt-1 block text-xs text-accent-800">{r.detail}</span>
          )}
        </span>
      ),
    },
    {
      key: 'do',
      header: '',
      /*
       * One button and a menu, not six words in a row.
       *
       * Every verb here used to be drawn inline at the same weight, wrapping
       * on a narrow row, with "It failed" sitting beside "Details" as though
       * they were the same size of decision.
       *
       * The primary is whichever single thing this row exists for right now:
       * send the token if there is one to send, otherwise nothing is promoted
       * — a row with no obvious next step should not have one invented for it,
       * because the invented one ends up under the cursor.
       */
      cell: (r) => (
        <RowActions
          actions={[
            ...(mayPay && r.can.send
              ? [
                  {
                    label: 'Send',
                    primary: true,
                    title: 'Send this token to the landlord',
                    onClick: () => onSend(r),
                  },
                ]
              : []),
            ...(r.source === 'purchase' && stageRows.some((one) => one.id === r.id)
              ? [
                  {
                    label: 'Details',
                    title: 'How far this payment has got, stage by stage',
                    onClick: () => setStagesFor(r.id),
                  },
                ]
              : []),
            ...(r.can.landlord
              ? [{ label: 'Landlord', onClick: () => onLandlord(r) }]
              : []),
            ...(mayEdit && r.can.assign
              ? [
                  {
                    label: 'File it',
                    title: 'Put this message under a landlord',
                    onClick: () => onAssign(r),
                  },
                ]
              : []),
            ...(mayEdit && r.can.correct
              ? [{ label: 'Correct', onClick: () => onCorrect(r) }]
              : []),
            ...(mayPay && r.can.edit ? [{ label: 'Edit', onClick: () => onEdit(r) }] : []),
            ...(mayPay && r.can.retry
              ? [
                  {
                    label: 'Retry',
                    disabled: retry.isPending,
                    title: 'Push this payment to the phone again',
                    onClick: () => retry.mutate(r.id),
                  },
                ]
              : []),
            ...(mayPay && r.can.fail
              ? [
                  {
                    label: 'It failed',
                    danger: true,
                    title: 'Close this off with a reason — the payment did not work',
                    onClick: () => onFail(r),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  const waiting = all.filter((one) => one.state === 'Waiting' || one.state === 'Needs a landlord');

  return (
    <section className="card" data-power-activity>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="font-heading text-lg">
          <button
            type="button"
            className="flex items-center gap-2 text-left"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`}
              aria-hidden="true"
            />
            Token messages extracted from the phone
            {waiting.length > 0 && (
              <span className="ml-2 rounded-full border border-accent-300 bg-accent-50 px-2 py-0.5 text-xs text-accent-900">
                {waiting.length} waiting
              </span>
            )}
          </button>
        </h2>

        {open && (
        <button
          type="button"
          className={showTrash ? 'btn-primary px-2 py-1 text-xs' : 'btn-quiet px-2 py-1 text-xs'}
          onClick={() => setShowTrash(!showTrash)}
          title="Messages put away are hidden by default. Nothing is ever deleted."
        >
          {showTrash ? 'Hide the trash' : 'Show the trash'}
        </button>
        )}
      </div>

      {open && (
        <>

      {discard.data && (
        <p className="mt-3 rounded-xl border border-secondary-300 bg-secondary-50 p-3 text-sm text-secondary-900">
          {discard.data.message}
        </p>
      )}

      {activity.isPending ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Looking&hellip;</p>
      ) : all.length === 0 ? (
        <EmptyState
          icon={ActivityIcon}
          headline="Nothing has happened yet"
          body="Pay a meter, or forward a message off the paying phone, and it appears here."
        />
      ) : (
        <>
          <Filters<Row>
            fields={FIELDS}
            query={query}
            onQuery={setQuery}
            clauses={clauses}
            onClauses={setClauses}
            total={all.length}
            showing={filtered.length}
            placeholder="Search by meter, landlord, receipt or sender"
          />

          <SelectionBar<Row>
            rows={filtered}
            selected={chosen}
            onClear={() => setChosen(new Set())}
            rowKey={(r) => `${r.source}:${r.id}`}
            exportName="power-activity"
            csv={[
              { header: 'When', value: (r) => new Date(r.at).toISOString() },
              { header: 'What', value: (r) => r.what },
              { header: 'Meter', value: (r) => r.meterLabel ?? '' },
              { header: 'Meter number', value: (r) => r.meterNumber ?? '' },
              { header: 'Landlord', value: (r) => r.landlordName ?? '' },
              { header: 'Amount', value: (r) => r.amount ?? '' },
              { header: 'Receipt', value: (r) => r.receipt ?? '' },
              { header: 'Sender', value: (r) => r.sender ?? '' },
              { header: 'State', value: (r) => r.state },
              { header: 'Detail', value: (r) => r.detail ?? '' },
              /*
               * No token column, not even the masked one. A CSV leaves this
               * system for a downloads folder, an email, a chat — and even
               * four digits of a bearer credential is four more than a
               * spreadsheet needs.
               */
            ]}
            actions={actions}
          />

          <DataTable<Row>
            rows={filtered}
            columns={columns}
            rowKey={(r) => `${r.source}:${r.id}`}
            selected={mayPay ? chosen : undefined}
            onSelect={mayPay ? setChosen : undefined}
            defaultSort={{ key: 'at', direction: 'desc' }}
            caption={`${filtered.length} ${filtered.length === 1 ? 'row' : 'rows'}`}
            empty={
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nothing matches those filters.
              </p>
            }
          />
        </>
      )}
        </>
      )}

      {stagesRow && (
        <Modal
          title={stagesRow.meterLabel}
          subtitle={`Meter ${stagesRow.meterNumber}`}
          onClose={() => setStagesFor(null)}
          footer={
            <button type="button" className="btn-quiet" onClick={() => setStagesFor(null)}>
              Close
            </button>
          }
        >
          <TokenProgress
            row={stagesRow}
            canDeliver={readiness.data?.sms.configured ?? true}
          />
        </Modal>
      )}
    </section>
  );
}

/** Where a row has got to, in a word and a colour. */
function StateChip({ state }: { state: string }) {
  /*
   * The colours come from the shared set now, not from a ladder of
   * comparisons here.
   *
   * This screen had its own palette, and so did four others — the same
   * meaning wearing a different colour depending which page you were on,
   * which taught nobody anything. `toneFor` is the one place that decides,
   * and an unrecognised state goes grey rather than guessing.
   */
  return <StatusPill tone={toneFor(state)}>{state}</StatusPill>;
}
